/**
 * Unsent composer text, keyed per conversation and bucketed per account. Module-level, not a controller ref:
 * the controller unmounts with the route and a draft has to outlive that. In memory only.
 */

/** Draft key for the unsent New chat — a real thread id can never collide with it. */
export const NEW_CHAT_DRAFT_KEY = "__new_chat_draft__"

const NEW_CHAT_DRAFT_SEPARATOR = "\n\n"

export function draftKeyForThread(threadId: string | null): string {
  return threadId ?? NEW_CHAT_DRAFT_KEY
}

interface AccountDrafts {
  byKey: Map<string, string>
  /** Threads created this session: prune-exempt until a list we observe carries them (the shared list lags an adoption). */
  adoptedThreadIds: Set<string>
}

const draftsByAccount = new Map<string, AccountDrafts>()

function accountDrafts(accountAlias: string): AccountDrafts {
  const existing = draftsByAccount.get(accountAlias)
  if (existing != null) return existing
  const created: AccountDrafts = { byKey: new Map(), adoptedThreadIds: new Set() }
  draftsByAccount.set(accountAlias, created)
  return created
}

export function readComposerDraft(accountAlias: string, key: string): string {
  return draftsByAccount.get(accountAlias)?.byKey.get(key) ?? ""
}

/** An empty value drops the entry, so a sent (or cleared) composer keeps no draft. */
export function writeComposerDraft(accountAlias: string, key: string, value: string): void {
  if (value === "") {
    draftsByAccount.get(accountAlias)?.byKey.delete(key)
    return
  }
  accountDrafts(accountAlias).byKey.set(key, value)
}

/**
 * One-shot seed for the New chat — a nudge prompt clicked elsewhere in the app (#4189). Loses to anything
 * already in that account's bucket: the store outlives a visit, so a seed must never overwrite text the user
 * left. Returns whether it applied, so a caller can skip the navigation a discarded seed does not deserve.
 */
export function seedNewChatDraft(accountAlias: string, text: string | null | undefined): boolean {
  if (!text) return false
  const drafts = accountDrafts(accountAlias)
  if (drafts.byKey.get(NEW_CHAT_DRAFT_KEY)) return false
  drafts.byKey.set(NEW_CHAT_DRAFT_KEY, text)
  return true
}

export function appendToNewChatDraft(accountAlias: string, text: string): void {
  if (!text) return
  const unsent = readComposerDraft(accountAlias, NEW_CHAT_DRAFT_KEY)
  writeComposerDraft(accountAlias, NEW_CHAT_DRAFT_KEY, unsent ? `${unsent}${NEW_CHAT_DRAFT_SEPARATOR}${text}` : text)
}

/** Mark a thread created this session, exempting its draft from the list-based prune. */
export function markDraftThreadAdopted(accountAlias: string, threadId: string): void {
  accountDrafts(accountAlias).adoptedThreadIds.add(threadId)
}

/** Explicit delete — the one signal that outranks the adoption exemption. */
export function forgetComposerDraft(accountAlias: string, threadId: string): void {
  const drafts = draftsByAccount.get(accountAlias)
  if (drafts == null) return
  drafts.byKey.delete(threadId)
  drafts.adoptedThreadIds.delete(threadId)
}

/**
 * Drop drafts for threads that no longer exist (deleted here or from the drawer). The New-chat sentinel and this
 * session's adoptions are exempt: list absence can't distinguish replication lag from a delete, so we bias to
 * keeping — a wrong prune loses typed text, a wrong keep is an unreachable map entry.
 */
export function pruneComposerDrafts(accountAlias: string, liveThreadIds: ReadonlySet<string>): void {
  const drafts = draftsByAccount.get(accountAlias)
  if (drafts == null) return
  for (const key of drafts.byKey.keys()) {
    if (key === NEW_CHAT_DRAFT_KEY || liveThreadIds.has(key) || drafts.adoptedThreadIds.has(key)) continue
    drafts.byKey.delete(key)
  }
}

/** Test seam: the store deliberately outlives any component tree, so tests must clear it between cases. */
export function resetComposerDrafts(): void {
  draftsByAccount.clear()
}
